import { useState, useEffect, useCallback } from 'react'
import {
  getCommentPreview,
  initiateCommentFetch,
  getCommentFetchStatus,
  getCommentAnalytics,
  hasCommentData,
} from '../lib/api'
import {
  CommentPreviewResponse,
  CommentFetchStatus,
  CommentAnalysisResponse,
} from '../lib/types'

interface UseEnhancedCommentsReturn {
  // Preview state
  preview: CommentPreviewResponse | null
  previewLoading: boolean
  previewError: string | null

  // Fetch state
  fetching: boolean
  progress: CommentFetchStatus | null

  // Results state
  results: CommentAnalysisResponse | null

  // Modal state
  showCreditModal: boolean
  setShowCreditModal: (show: boolean) => void

  // Already fetched state
  alreadyFetched: boolean

  // Insufficient credits state
  insufficientCredits: boolean
  creditsError: string | null

  // Actions
  loadPreview: () => Promise<void>
  initiateFetch: () => Promise<void>
  checkExistingProgress: () => Promise<void>
  loadResults: () => Promise<void>
}

export function useEnhancedComments(taskId: string): UseEnhancedCommentsReturn {
  // Preview state
  const [preview, setPreview] = useState<CommentPreviewResponse | null>(null)
  const [previewLoading, setPreviewLoading] = useState(false)
  const [previewError, setPreviewError] = useState<string | null>(null)

  // Fetch state
  const [fetching, setFetching] = useState(false)
  const [progress, setProgress] = useState<CommentFetchStatus | null>(null)

  // Results state
  const [results, setResults] = useState<CommentAnalysisResponse | null>(null)

  // Modal state
  const [showCreditModal, setShowCreditModal] = useState(false)

  // Already fetched state
  const [alreadyFetched, setAlreadyFetched] = useState(false)

  // Insufficient credits state
  const [insufficientCredits, setInsufficientCredits] = useState(false)
  const [creditsError, setCreditsError] = useState<string | null>(null)

  // Polling interval ref
  const [pollingInterval, setPollingInterval] = useState<NodeJS.Timeout | null>(
    null,
  )

  // Load preview on mount
  useEffect(() => {
    loadPreview()
    checkExistingProgress()
    checkIfAlreadyFetched()
  }, [taskId])

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (pollingInterval) {
        clearInterval(pollingInterval)
      }
    }
  }, [pollingInterval])

  const checkIfAlreadyFetched = async () => {
    try {
      const exists = await hasCommentData(taskId)
      setAlreadyFetched(exists)

      // If already fetched, load the results
      if (exists) {
        await loadResults()
      }
    } catch (error) {
      console.error('Error checking if comments already fetched:', error)
    }
  }

  const loadPreview = useCallback(async () => {
    setPreviewLoading(true)
    setPreviewError(null)

    try {
      const data = await getCommentPreview(taskId)
      setPreview(data)
    } catch (error) {
      console.error('Error loading preview:', error)
      setPreviewError(
        error instanceof Error ? error.message : 'Failed to load preview',
      )
    } finally {
      setPreviewLoading(false)
    }
  }, [taskId])

  const checkExistingProgress = useCallback(async () => {
    try {
      const status = await getCommentFetchStatus(taskId)

      if (status.status === 'in_progress') {
        setFetching(true)
        setProgress(status)
        startPolling()
      } else if (status.status === 'completed') {
        setProgress(status)
        await loadResults()
      }
    } catch (error) {
      // No existing progress, that's fine
      console.log('No existing progress found')
    }
  }, [taskId])

  const initiateFetch = useCallback(async () => {
    setFetching(true)
    setShowCreditModal(false)
    setInsufficientCredits(false)
    setCreditsError(null)

    try {
      const response = await initiateCommentFetch({
        task_id: taskId,
        get_all: true,
      })

      if (response.success) {
        setProgress({
          status: response.status,
          comments_fetched: response.comments_fetched,
          estimated_total: response.estimated_total,
          progress_percent: 0,
        })
        startPolling()
      } else {
        throw new Error('Failed to initiate fetch')
      }
    } catch (error) {
      console.error('Error initiating fetch:', error)
      setFetching(false)

      const errorMessage =
        error instanceof Error ? error.message : 'Failed to initiate fetch'

      // Check if it's an insufficient credits error
      if (
        errorMessage.toLowerCase().includes('insufficient credits') ||
        errorMessage.toLowerCase().includes('payment required')
      ) {
        setInsufficientCredits(true)
        setCreditsError(errorMessage)
      } else {
        setPreviewError(errorMessage)
      }
    }
  }, [taskId])

  const startPolling = useCallback(() => {
    // Clear any existing interval
    if (pollingInterval) {
      clearInterval(pollingInterval)
    }

    const interval = setInterval(async () => {
      try {
        const status = await getCommentFetchStatus(taskId)
        setProgress(status)

        if (status.status === 'completed') {
          clearInterval(interval)
          setPollingInterval(null)
          setFetching(false)
          await loadResults()
        } else if (status.status === 'failed') {
          clearInterval(interval)
          setPollingInterval(null)
          setFetching(false)
          setPreviewError(status.error_message || 'Fetch failed')
        }
      } catch (error) {
        console.error('Error polling status:', error)
      }
    }, 2000) // Poll every 2 seconds

    setPollingInterval(interval)
  }, [taskId, pollingInterval])

  const loadResults = useCallback(async () => {
    try {
      const data = await getCommentAnalytics({
        task_id: taskId,
        limit: 50,
      })
      setResults(data)
    } catch (error) {
      console.error('Error loading results:', error)
    }
  }, [taskId])

  return {
    preview,
    previewLoading,
    previewError,
    fetching,
    progress,
    results,
    showCreditModal,
    setShowCreditModal,
    alreadyFetched,
    insufficientCredits,
    creditsError,
    loadPreview,
    initiateFetch,
    checkExistingProgress,
    loadResults,
  }
}
